import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LvdProductComplianceFileDeclarationOfConformityGenerateDialog } from '@/v2/components/lvd/LvdProductComplianceFileDeclarationOfConformityGenerateDialog'
import { V2ProductDescriptionEditDialog } from '@/v2/components/rohs/V2ProductDescriptionEditDialog'
import { V2LvdApplicabilityStep } from '@/v2/components/lvd-legislation-briefing/V2LvdApplicabilityStep'
import { V2LvdComplianceFileSlotStep } from '@/v2/components/lvd-legislation-briefing/V2LvdComplianceFileSlotStep'
import { V2LvdConfirmedStandardsEditSheet } from '@/v2/components/lvd-legislation-briefing/V2LvdConfirmedStandardsEditSheet'
import { V2LvdConfirmedStandardsStep } from '@/v2/components/lvd-legislation-briefing/V2LvdConfirmedStandardsStep'
import { evaluateLvdProductComplianceFileDeclarationOfConformityRequirements } from '@/v2/lib/lvdProductComplianceFileDeclarationOfConformityRequirements'
import { V2LvdEuDeclarationStep } from '@/v2/components/lvd-legislation-briefing/V2LvdEuDeclarationStep'
import { V2LvdManufacturerInformationStep } from '@/v2/components/lvd-legislation-briefing/V2LvdManufacturerInformationStep'
import { V2LvdProductDescriptionStep } from '@/v2/components/lvd-legislation-briefing/V2LvdProductDescriptionStep'
import { V2LvdProductIdentificationStep } from '@/v2/components/lvd-legislation-briefing/V2LvdProductIdentificationStep'
import { V2LvdProductSignOffStep } from '@/v2/components/lvd-legislation-briefing/V2LvdProductSignOffStep'
import { V2LvdCapabilitiesList } from '@/v2/components/lvd-legislation-briefing/V2LvdCapabilitiesList'
import { V2LvdRequiredNextBanner } from '@/v2/components/lvd-legislation-briefing/V2LvdRequiredNextBanner'
import { useGetLvdProductComplianceFiles } from '@/shared/hooks/api/lvdComplianceFiles'
import {
  isLvdComplianceFileSlotFilled,
  useGetLvdComplianceFileSlot,
} from '@/shared/hooks/api/lvdProductComplianceFileAttachments'
import { useGetProduct } from '@/shared/hooks/api/product'
import {
  useGetProductSharedEvidence,
  isProductSharedEvidenceSlotFilled,
} from '@/shared/hooks/api/productSharedEvidence'
import { useCanWriteOrgData } from '@/shared/hooks/useCanWriteOrgData'
import { BriefingStepList } from '@/shared/briefing'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Skeleton } from '@/components/ui/skeleton'
import {
  buildLvdLegislationBriefing,
  findFirstIncompleteRequiredStep,
  parseConfirmedLvdStandards,
} from '../utils/lvdLegislationBriefing'

export function V2LvdLegislationBriefing({
  productId,
  lvdApplies,
}: {
  productId: string
  lvdApplies: boolean
}) {
  const { t } = useTranslation('common')
  const { data: product } = useGetProduct(productId)
  const { data: sharedEvidence } = useGetProductSharedEvidence(productId)
  const { data: drawings } = useGetLvdComplianceFileSlot(
    productId,
    'DRAWINGS_SCHEMAS',
    { enabled: lvdApplies }
  )
  const { data: standardsTestReport } = useGetLvdComplianceFileSlot(
    productId,
    'STANDARDS_TEST_REPORT',
    { enabled: lvdApplies }
  )
  const { data: examinationsOrCalculations } = useGetLvdComplianceFileSlot(
    productId,
    'EXAMINATIONS_OR_CALCULATIONS',
    { enabled: lvdApplies }
  )
  const { data: instructionsAndSafetyInformation } = useGetLvdComplianceFileSlot(
    productId,
    'INSTRUCTIONS_AND_SAFETY_INFORMATION',
    { enabled: lvdApplies }
  )
  const {
    data: files,
    isLoading,
    isError,
  } = useGetLvdProductComplianceFiles(productId, {
    enabled: lvdApplies,
  })
  const canWrite = useCanWriteOrgData()
  const [descriptionDialogOpen, setDescriptionDialogOpen] = useState(false)
  const [confirmedStandardsSheetOpen, setConfirmedStandardsSheetOpen] =
    useState(false)
  const [declarationDialogOpen, setDeclarationDialogOpen] = useState(false)
  const [declarationProgress, setDeclarationProgress] = useState<
    string | null
  >(null)

  if (!lvdApplies) {
    return <V2LvdCapabilitiesList />
  }

  if (isLoading) {
    return (
      <div className='space-y-3' aria-busy='true'>
        <Skeleton className='h-20 w-full' />
        <Skeleton className='h-40 w-full' />
      </div>
    )
  }

  if (isError || files == null) {
    return (
      <p className='text-sm text-muted-foreground'>
        {t('v2.pages.products.detail.lvdFiles.loadFailed')}
      </p>
    )
  }

  const hasProductDescription =
    product?.description != null && product.description.trim() !== ''
  const briefing = buildLvdLegislationBriefing({
    hasProductDescription,
    hasProductIdentification: isProductSharedEvidenceSlotFilled(
      sharedEvidence,
      'PRODUCT_IDENTIFICATION'
    ),
    hasManufacturerInformation: isProductSharedEvidenceSlotFilled(
      sharedEvidence,
      'MANUFACTURER_INFORMATION'
    ),
    slotFilled: {
      DRAWINGS_SCHEMAS: isLvdComplianceFileSlotFilled(drawings),
      STANDARDS_TEST_REPORT: isLvdComplianceFileSlotFilled(standardsTestReport),
      EXAMINATIONS_OR_CALCULATIONS: isLvdComplianceFileSlotFilled(
        examinationsOrCalculations
      ),
      INSTRUCTIONS_AND_SAFETY_INFORMATION: isLvdComplianceFileSlotFilled(
        instructionsAndSafetyInformation
      ),
    },
    files,
  })
  const firstIncompleteRequiredStep = findFirstIncompleteRequiredStep(
    briefing.steps
  )
  const productFile = files.product
  const confirmedStandards = parseConfirmedLvdStandards(
    productFile?.confirmedStandardsJson ?? null
  )
  const declarationEvaluation =
    productFile != null
      ? evaluateLvdProductComplianceFileDeclarationOfConformityRequirements({
          productName: product?.name,
          productSku: product?.sku,
          productDescription: product?.description,
          lvdApplies,
          productFile,
        })
      : null
  let declarationLabel = t(
    'v2.pages.products.detail.lvdBriefing.steps.items.euDeclaration.action'
  )
  if (declarationProgress != null) {
    declarationLabel = declarationProgress
  }

  const [
    applicability,
    productDescription,
    productIdentification,
    manufacturerInformation,
    confirmedStandardsStep,
    productDrawings,
    standardsTestReportStep,
    examinationsOrCalculationsStep,
    instructionsAndSafetyInformationStep,
    euDeclaration,
    productSignOff,
  ] = briefing.steps

  return (
    <div className='space-y-6'>
      <V2LvdRequiredNextBanner
        firstIncompleteStep={firstIncompleteRequiredStep}
      />

      <Alert variant='info'>
        <AlertTitle className='line-clamp-none'>
          {t('v2.pages.products.detail.lvdBriefing.overview.title')}
        </AlertTitle>
        <AlertDescription className='space-y-2'>
          <p>{t('v2.pages.products.detail.lvdBriefing.overview.body')}</p>
        </AlertDescription>
      </Alert>

      <V2LvdCapabilitiesList />

      <BriefingStepList
        title={t('v2.pages.products.detail.lvdBriefing.steps.title')}
        description={t('v2.pages.products.detail.lvdBriefing.steps.description')}
      >
        <V2LvdApplicabilityStep step={applicability} index={0} />
        <V2LvdProductDescriptionStep
          step={productDescription}
          index={1}
          description={product?.description}
          hasProductDescription={hasProductDescription}
          canWrite={canWrite}
          onEditDescription={() => {
            setDescriptionDialogOpen(true)
          }}
        />
        <V2LvdProductIdentificationStep
          step={productIdentification}
          index={2}
          productId={productId}
        />
        <V2LvdManufacturerInformationStep
          step={manufacturerInformation}
          index={3}
          productId={productId}
        />
        <V2LvdConfirmedStandardsStep
          step={confirmedStandardsStep}
          index={4}
          standards={confirmedStandards}
          canWrite={canWrite}
          onEditStandards={() => {
            setConfirmedStandardsSheetOpen(true)
          }}
        />
        <V2LvdComplianceFileSlotStep
          step={productDrawings}
          index={5}
          productId={productId}
          slotKind='DRAWINGS_SCHEMAS'
        />
        <V2LvdComplianceFileSlotStep
          step={standardsTestReportStep}
          index={6}
          productId={productId}
          slotKind='STANDARDS_TEST_REPORT'
        />
        <V2LvdComplianceFileSlotStep
          step={examinationsOrCalculationsStep}
          index={7}
          productId={productId}
          slotKind='EXAMINATIONS_OR_CALCULATIONS'
        />
        <V2LvdComplianceFileSlotStep
          step={instructionsAndSafetyInformationStep}
          index={8}
          productId={productId}
          slotKind='INSTRUCTIONS_AND_SAFETY_INFORMATION'
        />
        <V2LvdEuDeclarationStep
          step={euDeclaration}
          index={9}
          declarationLabel={declarationLabel}
          declarationEvaluation={declarationEvaluation}
          onOpenDeclarationDialog={() => {
            setDeclarationDialogOpen(true)
          }}
        />
        <V2LvdProductSignOffStep
          step={productSignOff}
          index={10}
          productId={productId}
          productFile={productFile}
        />
      </BriefingStepList>
      <V2ProductDescriptionEditDialog
        productId={productId}
        description={product?.description}
        open={descriptionDialogOpen}
        onOpenChange={setDescriptionDialogOpen}
      />
      {confirmedStandardsSheetOpen ? (
        <V2LvdConfirmedStandardsEditSheet
          productId={productId}
          standards={confirmedStandards}
          open={confirmedStandardsSheetOpen}
          onOpenChange={setConfirmedStandardsSheetOpen}
        />
      ) : null}
      {productFile != null ? (
        <LvdProductComplianceFileDeclarationOfConformityGenerateDialog
          open={declarationDialogOpen}
          onOpenChange={setDeclarationDialogOpen}
          productId={productId}
          productName={product?.name}
          productSku={product?.sku}
          productDescription={product?.description}
          lvdApplies={lvdApplies}
          productFile={productFile}
          outputDocuments={files.outputDocuments}
          onProgressMessage={setDeclarationProgress}
        />
      ) : null}
    </div>
  )
}
